import React, { useEffect, useState } from "react";
import { TextField } from "@material-ui/core";
import { ContextLinkBtn } from "../styles";
import { LABELS } from "../labels";
import type { TextRegistryScope } from "../types";

interface Props {
	scope: TextRegistryScope;
	entryKey: string;
	screen: string;
}

// Per-scope default dev URLs. Override via localStorage `whalelo.previewBaseUrl.{scope}`.
const SCOPE_DEFAULT_BASE_URLS: Record<TextRegistryScope, string> = {
	admin: typeof window !== "undefined" ? window.location.origin : "http://localhost:8400",
	homepage: "http://localhost:3001",
	stations: "http://localhost:3018",
};

const LS_BASE_PREFIX = "whalelo.previewBaseUrl";
const LS_OVERRIDE_PREFIX = "whalelo.previewOverride";

function getStoredBase(scope: TextRegistryScope): string {
	if (typeof localStorage === "undefined") return SCOPE_DEFAULT_BASE_URLS[scope];
	return localStorage.getItem(`${LS_BASE_PREFIX}.${scope}`) || SCOPE_DEFAULT_BASE_URLS[scope];
}

function getStoredOverride(scope: TextRegistryScope, screen: string): string {
	if (typeof localStorage === "undefined") return "";
	return localStorage.getItem(`${LS_OVERRIDE_PREFIX}.${scope}.${screen}`) || "";
}

function setStoredOverride(scope: TextRegistryScope, screen: string, url: string) {
	if (typeof localStorage === "undefined") return;
	const key = `${LS_OVERRIDE_PREFIX}.${scope}.${screen}`;
	if (url) localStorage.setItem(key, url);
	else localStorage.removeItem(key);
}

function buildAutoUrl(scope: TextRegistryScope, screen: string, key: string): string {
	const base = getStoredBase(scope).replace(/\/$/, "");
	const path = `/${screen}`.replace(/\/+/g, "/");
	return `${base}${path}?__textkey=${encodeURIComponent(key)}`;
}

export default function ContextDeeplink({ scope, entryKey, screen }: Props) {
	const [override, setOverride] = useState("");
	const [showAdvanced, setShowAdvanced] = useState(false);

	useEffect(() => {
		setOverride(getStoredOverride(scope, screen));
	}, [scope, screen]);

	if (!screen || !entryKey) {
		return <p style={{ color: "#94a3b8", fontSize: "0.875rem" }}>{LABELS.contextNoUrl}</p>;
	}

	const autoUrl = buildAutoUrl(scope, screen, entryKey);
	const finalUrl = override.trim() || autoUrl;

	function handleSaveOverride() {
		setStoredOverride(scope, screen, override.trim());
	}

	function handleClearOverride() {
		setOverride("");
		setStoredOverride(scope, screen, "");
	}

	return (
		<div>
			<p style={{ fontSize: "0.8125rem", color: "#475569", marginBottom: 8, lineHeight: 1.5 }}>
				Auto-built URL based on <strong>{scope}</strong> base + screen <code>{screen}</code>. Click to open;
				target app must handle <code>__textkey</code> to scroll. <strong>If 404</strong>, set a manual override
				below.
			</p>

			<div
				style={{
					fontSize: "0.75rem",
					color: "#64748b",
					background: "#f8fafc",
					border: "1px solid #e2e8f0",
					borderRadius: 6,
					padding: "8px 10px",
					marginBottom: 10,
					wordBreak: "break-all",
					fontFamily: "Fira Code, Courier New, monospace",
				}}>
				{finalUrl}
			</div>

			<ContextLinkBtn
				href={finalUrl}
				target="_blank"
				rel="noopener noreferrer">
				<i className="fa fa-external-link" />
				{LABELS.contextViewOnSite}
			</ContextLinkBtn>

			<div style={{ marginTop: 12 }}>
				<button
					type="button"
					onClick={() => setShowAdvanced((s) => !s)}
					style={{
						background: "transparent",
						border: "none",
						color: "#2563eb",
						fontSize: "0.75rem",
						cursor: "pointer",
						padding: 0,
					}}>
					{showAdvanced ? "▾ Hide override" : "▸ Set custom URL for this screen"}
				</button>
			</div>

			{showAdvanced && (
				<div style={{ marginTop: 10 }}>
					<TextField
						fullWidth
						size="small"
						variant="outlined"
						label={`Override URL for ${scope}/${screen}`}
						placeholder={autoUrl}
						value={override}
						onChange={(e) => setOverride(e.target.value)}
						helperText="Saved in browser per (scope, screen). Use full URL incl. http://"
					/>
					<div style={{ display: "flex", gap: 6, marginTop: 6 }}>
						<button
							type="button"
							onClick={handleSaveOverride}
							style={{
								padding: "5px 12px",
								borderRadius: 6,
								border: "1px solid #2563eb",
								background: "#2563eb",
								color: "#fff",
								fontSize: "0.75rem",
								cursor: "pointer",
								fontWeight: 600,
							}}>
							Save override
						</button>
						<button
							type="button"
							onClick={handleClearOverride}
							style={{
								padding: "5px 12px",
								borderRadius: 6,
								border: "1px solid #e2e8f0",
								background: "#fff",
								color: "#64748b",
								fontSize: "0.75rem",
								cursor: "pointer",
							}}>
							Clear
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
